import { FormSelect } from "../../../components/ui/select";
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { AlertTriangle, Upload, Trash, FileText, Building, Users, Scale, Landmark, Briefcase, ClipboardList, Settings } from 'lucide-react';
import i18n from "../../../i18n";
import { AppLayout, PageBackLink } from '../../../components/layout';
import { PageHeader } from '../../../components/ui';
import '../inventory.css';
import {
  fetchVendorById,
  createVendor,
  updateVendor,
  approveVendor,
  mapFormToApiPayload,
  fetchVendorFormLookups,
  VENDOR_TYPE_OPTIONS,
} from '../../../services/productVendorService';

const TOAST_KEY = 'product_vendor_toast';

function translateVendorStatus(status) {
  if (status === 'Active') return i18n.t('status.active');
  if (status === 'Inactive') return i18n.t('status.inactive');
  if (status === 'Blacklisted') return i18n.t('inventory.blacklisted');
  return status;
}

function translateRiskLevelLabel(riskLevel) {
  if (riskLevel === 'Low') return i18n.t('inventory.lowRisk');
  if (riskLevel === 'Medium') return i18n.t('inventory.mediumRisk');
  if (riskLevel === 'High') return i18n.t('inventory.highRisk');
  return riskLevel ? i18n.t('inventory.riskLevelWithValue', { riskLevel }) : riskLevel;
}

function translateSupplierType(type) {
  if (type === 'Manufacturer') return i18n.t('crm.manufacturer');
  if (type === 'OEM') return i18n.t('inventory.oem');
  if (type === 'Distributor') return i18n.t('inventory.distributor');
  return type;
}

function translateOptionLabel(value, label) {
  if (value === 'Active' || value === 'Inactive' || value === 'Blacklisted') {
    return translateVendorStatus(value);
  }
  if (value === 'Low' || value === 'Medium' || value === 'High') {
    return translateRiskLevelLabel(value);
  }
  if (value === 'Manufacturer' || value === 'OEM' || value === 'Distributor') {
    return translateSupplierType(value);
  }
  return label || value;
}

export default function AddEditVendor() {
  const { vendorId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditRoute = Boolean(vendorId);

  const [loading, setLoading] = useState(isEditRoute);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [riskLevelOptions, setRiskLevelOptions] = useState([]);
  const [vendorTypeOptions, setVendorTypeOptions] = useState(VENDOR_TYPE_OPTIONS);
  const [vendorStatusOptions, setVendorStatusOptions] = useState([]);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [approverOptions, setApproverOptions] = useState([]);
  const [lookupsLoading, setLookupsLoading] = useState(true);

  // const onCancel = () => {
  //   if (isEditRoute) {
  //     navigate(`/IMS/inventory/vendors/${vendorId}`);
  //   } else {
  //     navigate('/IMS/inventory/vendors');
  //   }
  // };

  const onCancel = () => {
  if (!isEditRoute) {
    navigate('/IMS/inventory/vendors');
    return;
  }

  if (location.state?.from === 'catalog') {
    navigate('/IMS/inventory/vendors');
  } else {
    navigate(`/IMS/inventory/vendors/${vendorId}`);
  }
};

  // Local Form States
  const [tradeName, setTradeName] = useState('');
  const [legalCompanyName, setLegalCompanyName] = useState('');
  const [vendorType, setVendorType] = useState('');
  const [companyRegNumber, setCompanyRegNumber] = useState('');

  const [primaryName, setPrimaryName] = useState('');
  const [primaryMobile, setPrimaryMobile] = useState('');
  const [primaryEmail, setPrimaryEmail] = useState('');
  const [secondaryName, setSecondaryName] = useState('');
  const [secondaryMobile, setSecondaryMobile] = useState('');
  const [secondaryEmail, setSecondaryEmail] = useState('');
  const [alternatePhone, setAlternatePhone] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [addressCity, setAddressCity] = useState('');
  const [addressState, setAddressState] = useState('');
  const [addressPincode, setAddressPincode] = useState('');

  const [gstin, setGstin] = useState('');
  const [pan, setPan] = useState('');
  const [msmeRegNo, setMsmeRegNo] = useState('');
  const [cin, setCin] = useState('');
  const [tan, setTan] = useState('');
  const [iec, setIec] = useState('');
  const [isoCertifications, setIsoCertifications] = useState('');

  const [bankName, setBankName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [ifscCode, setIfscCode] = useState('');
  const [swiftCode, setSwiftCode] = useState('');
  const [upiId, setUpiId] = useState('');

  const [productsOffered, setProductsOffered] = useState('');
  const [isAuthorized, setIsAuthorized] = useState('');

  const [paymentTerms, setPaymentTerms] = useState('');
  const [creditPeriod, setCreditPeriod] = useState('');
  const [currency, setCurrency] = useState('INR');

  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);

  const [vendorStatus, setVendorStatus] = useState('Active');
  const [approvedBy, setApprovedBy] = useState('');
  const [approverLabel, setApproverLabel] = useState('');
  const [approvalDate, setApprovalDate] = useState('');
  const [riskLevel, setRiskLevel] = useState('Low');

  const [activeTab, setActiveTab] = useState('basic');

  const [errors, setErrors] = useState({});
  const [validationAlert, setValidationAlert] = useState(null);

  /**
   * Vendor Type is fixed (Manufacturer | OEM | Distributor) — no API.
   * Vendor Status, Risk Level, Currency and Approved By use API lookups.
   */
  useEffect(() => {
    let cancelled = false;
    setLookupsLoading(true);
    setVendorTypeOptions(VENDOR_TYPE_OPTIONS);
    fetchVendorFormLookups({ size: 100 })
      .then((lookups) => {
        if (cancelled) return;
        setVendorTypeOptions(lookups.supplierTypeOptions?.length ? lookups.supplierTypeOptions : VENDOR_TYPE_OPTIONS);
        setVendorStatusOptions(lookups.vendorStatusOptions || []);
        setRiskLevelOptions(lookups.riskLevelOptions || []);
        setCurrencyOptions(lookups.currencyOptions || []);
        setApproverOptions(lookups.approverOptions || []);

        if (!isEditRoute) {
          const pick = (options, preferred) => {
            const list = options || [];
            const match = list.find((o) => o.value === preferred);
            return match?.value || list[0]?.value || '';
          };
          setRiskLevel((prev) =>
            prev && (lookups.riskLevelOptions || []).some((o) => o.value === prev)
              ? prev
              : pick(lookups.riskLevelOptions, 'Low')
          );
          setVendorStatus((prev) =>
            prev && (lookups.vendorStatusOptions || []).some((o) => o.value === prev)
              ? prev
              : pick(lookups.vendorStatusOptions, 'Active')
          );
          setCurrency((prev) =>
            prev && (lookups.currencyOptions || []).some((o) => o.value === prev)
              ? prev
              : pick(lookups.currencyOptions, 'INR')
          );
        }
      })
      .catch(() => {
        if (cancelled) return;
        setVendorTypeOptions(VENDOR_TYPE_OPTIONS);
        setVendorStatusOptions([]);
        setRiskLevelOptions([]);
        setCurrencyOptions([]);
        setApproverOptions([]);
      })
      .finally(() => {
        if (!cancelled) setLookupsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isEditRoute]);

  /** A saved value must stay selectable even when its lookup does not return it. */
  useEffect(() => {
    const ensure = (setOptions, value, label) => {
      if (!value) return;
      setOptions((prev) =>
        prev.some((o) => o.value === value) ? prev : [...prev, { value, label: label || value }]
      );
    };
    ensure(setVendorTypeOptions, vendorType);
    ensure(setVendorStatusOptions, vendorStatus);
    ensure(setCurrencyOptions, currency);
    ensure(setRiskLevelOptions, riskLevel);
    ensure(setApproverOptions, approvedBy, approverLabel);
  }, [
    vendorType,
    vendorStatus,
    currency,
    riskLevel,
    approvedBy,
    approverLabel,
    vendorTypeOptions,
    vendorStatusOptions,
    currencyOptions,
    riskLevelOptions,
    approverOptions,
  ]);

  useEffect(() => {
    if (!isEditRoute) return undefined;
    let cancelled = false;

    async function load() {
      setLoading(true);
      setLoadError(null);
      try {
        const vendor = await fetchVendorById(vendorId);
        if (cancelled) return;
        if (!vendor) {
          setLoadError(i18n.t('inventory.vendorNotFound'));
          return;
        }
        setTradeName(vendor.tradeName || '');
        setLegalCompanyName(vendor.legalCompanyName || '');
        setVendorType(vendor.type || '');
        setCompanyRegNumber(vendor.companyRegNumber || '');
        setPrimaryName(vendor.contactPerson || '');
        setPrimaryMobile(vendor.phone || '');
        setPrimaryEmail(vendor.email || '');
        setSecondaryName(vendor.secondaryName || '');
        setSecondaryMobile(vendor.secondaryMobile || '');
        setSecondaryEmail(vendor.secondaryEmail || '');
        setAlternatePhone(vendor.alternatePhone || '');
        setAddressLine1(vendor.addressLine1 || '');
        setAddressLine2(vendor.addressLine2 || '');
        setAddressCity(vendor.addressCity || '');
        setAddressState(vendor.addressState || '');
        setAddressPincode(vendor.addressPincode || '');
        setGstin(vendor.taxId || '');
        setPan(vendor.pan || '');
        setMsmeRegNo(vendor.msmeRegNo || '');
        setCin(vendor.cin || '');
        setTan(vendor.tan || '');
        setIec(vendor.iec || '');
        setIsoCertifications(vendor.isoCertifications || '');
        setBankName(vendor.bankName || '');
        setAccountNumber(vendor.accountNumber || '');
        setIfscCode(vendor.ifscCode || '');
        setSwiftCode(vendor.swiftCode || '');
        setUpiId(vendor.upiId || '');
        setProductsOffered(vendor.description || '');
        setIsAuthorized(vendor.isAuthorized || '');
        setPaymentTerms(vendor.paymentTerms || '');
        setCreditPeriod(vendor.creditPeriod !== undefined && vendor.creditPeriod !== null ? vendor.creditPeriod : '');
        setCurrency(vendor.currency || 'INR');
        setUploadedFiles(vendor.uploadedFiles || []);
        setVendorStatus(vendor.status || 'Active');
        setApprovedBy(vendor.approvedBy || '');
        setApproverLabel(
          vendor.approvedByName || (vendor.approvedBy ? i18n.t('inventory.employeeNumber', { id: vendor.approvedBy }) : '')
        );
        setApprovalDate(vendor.approvalDate || '');
        setRiskLevel(vendor.riskLevel || 'Low');
      } catch (err) {
        if (!cancelled) setLoadError(err?.message || i18n.t('inventory.failedToLoadVendor'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [vendorId, isEditRoute]);

  const tabs = [
    { id: 'basic', label: i18n.t('inventory.tabBasicInfo'), icon: Building },
    { id: 'contacts', label: i18n.t('inventory.tabContacts'), icon: Users },
    { id: 'tax', label: i18n.t('inventory.tabTaxCompliance'), icon: Scale },
    { id: 'banking', label: i18n.t('inventory.tabBanking'), icon: Landmark },
    { id: 'business', label: i18n.t('inventory.tabBusiness'), icon: Briefcase },
    { id: 'commercial', label: i18n.t('inventory.tabCommercial'), icon: ClipboardList },
    { id: 'documents', label: i18n.t('inventory.tabDocuments'), icon: FileText },
    { id: 'internal', label: i18n.t('inventory.tabInternal'), icon: Settings },
  ];

  const hasTabErrors = (tabId) => {
    if (tabId === 'basic') return !!(errors.tradeName || errors.vendorType);
    if (tabId === 'contacts') return !!(errors.primaryName || errors.primaryMobile || errors.primaryEmail);
    if (tabId === 'tax') return !!(errors.gstin || errors.pan);
    if (tabId === 'banking') return !!(errors.bankName || errors.accountNumber || errors.ifscCode);
    if (tabId === 'commercial') return !!(errors.paymentTerms || errors.currency);
    if (tabId === 'internal') return !!(errors.vendorStatus || errors.riskLevel);
    return false;
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileSelect(e.target.files);
      e.target.value = '';
    }
  };

  const handleFileSelect = (files) => {
    const newDocs = Array.from(files).map((f) => ({
      id: `doc-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
      name: f.name,
      size: f.size ? `${(f.size / (1024 * 1024)).toFixed(2)}${i18n.t('inventory.megabyteUnit')}` : null,
      uploadedAt: new Date().toISOString().split('T')[0]
    }));
    setUploadedFiles((prev) => [...prev, ...newDocs]);
  };

  const handleRemoveFile = (fileId) => {
    setUploadedFiles(uploadedFiles.filter(f => f.id !== fileId));
  };

  const validateTab = (tabId) => {
    const newErrors = {};
    const required = (fieldKey) => i18n.t('validation.fieldRequired', { field: i18n.t(fieldKey) });

    if (tabId === 'basic') {
      if (!tradeName.trim()) newErrors.tradeName = required('inventory.tradeName');
      if (!vendorType) newErrors.vendorType = required('inventory.vendorType');
    }

    if (tabId === 'contacts') {
      if (!primaryName.trim()) newErrors.primaryName = required('inventory.contactName');
      if (!primaryMobile.trim()) newErrors.primaryMobile = required('inventory.mobileNumber');
      if (!primaryEmail.trim()) newErrors.primaryEmail = required('inventory.emailAddress');
    }

    if (tabId === 'tax') {
      if (!gstin.trim()) newErrors.gstin = required('inventory.gstin');
      if (!pan.trim()) newErrors.pan = required('inventory.pan');
    }

    if (tabId === 'banking' && vendorType === 'Distributor') {
      if (!bankName.trim()) newErrors.bankName = required('inventory.bankNameBranch');
      if (!accountNumber.trim()) newErrors.accountNumber = required('inventory.accountNameNumber');
      if (!ifscCode.trim()) newErrors.ifscCode = required('inventory.ifscCode');
    }

    if (tabId === 'commercial') {
      if (!currency) newErrors.currency = required('productMetrics.currency');
      if (vendorType === 'Distributor' && !paymentTerms.trim()) {
        newErrors.paymentTerms = required('inventory.standardPaymentTerms');
      }
    }

    if (tabId === 'internal') {
      if (!vendorStatus) newErrors.vendorStatus = required('inventory.vendorStatus');
      if (!riskLevel) newErrors.riskLevel = required('inventory.riskLevelLabel');
    }

    return newErrors;
  };

  const buildFormPayload = () => ({
    tradeName: tradeName.trim(),
    legalCompanyName: legalCompanyName.trim(),
    vendorType,
    companyRegNumber: companyRegNumber.trim(),
    primaryName: primaryName.trim(),
    primaryMobile: primaryMobile.trim(),
    primaryEmail: primaryEmail.trim(),
    secondaryName: secondaryName.trim(),
    secondaryMobile: secondaryMobile.trim(),
    secondaryEmail: secondaryEmail.trim(),
    alternatePhone: alternatePhone.trim(),
    addressLine1: addressLine1.trim(),
    addressLine2: addressLine2.trim(),
    addressCity: addressCity.trim(),
    addressState: addressState.trim(),
    addressPincode: addressPincode.trim(),
    gstin: gstin.trim(),
    pan: pan.trim(),
    msmeRegNo: msmeRegNo.trim(),
    cin: cin.trim(),
    tan: tan.trim(),
    iec: iec.trim(),
    bankName: bankName.trim(),
    accountNumber: accountNumber.trim(),
    ifscCode: ifscCode.trim(),
    swiftCode: swiftCode.trim(),
    upiId: upiId.trim(),
    productsOffered: productsOffered.trim(),
    // Kept for UI state / future API support; mapFormToApiPayload owns the wire body.
    isoCertifications: isoCertifications.trim(),
    isAuthorized,
    paymentTerms: paymentTerms.trim(),
    creditPeriod: creditPeriod !== '' ? Number(creditPeriod) : undefined,
    currency,
    uploadedFiles,
    vendorStatus,
    approvedBy: approvedBy || undefined,
    approvalDate: approvalDate || undefined,
    riskLevel,
  });

  const currentTabIndex = tabs.findIndex((t) => t.id === activeTab);
  const isFirstTab = currentTabIndex <= 0;
  const isLastTab = currentTabIndex === tabs.length - 1;

  /** Intermediate tabs: validate current tab only, keep shared state, go next. No API. */
  const handleSaveAndNext = () => {
    const tabErrors = validateTab(activeTab);
    if (Object.keys(tabErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...tabErrors }));
      setValidationAlert(i18n.t('inventory.completeRequiredFieldsOnTab'));
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Clear errors for the current tab only; retain data in shared form state.
    const cleared = { ...errors };
    Object.keys(tabErrors).forEach((k) => {
      delete cleared[k];
    });
    // Also clear known keys for this tab even if they were previously set
    const tabKeys = {
      basic: ['tradeName', 'vendorType'],
      contacts: ['primaryName', 'primaryMobile', 'primaryEmail'],
      tax: ['gstin', 'pan'],
      banking: ['bankName', 'accountNumber', 'ifscCode'],
      commercial: ['paymentTerms', 'currency'],
      internal: ['vendorStatus', 'riskLevel'],
    };
    (tabKeys[activeTab] || []).forEach((k) => {
      delete cleared[k];
    });

    setErrors(cleared);
    setValidationAlert(null);

    if (!isLastTab) {
      setActiveTab(tabs[currentTabIndex + 1].id);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBackTab = () => {
    if (isFirstTab) return;
    setValidationAlert(null);
    setActiveTab(tabs[currentTabIndex - 1].id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  /**
   * Final step only: validate all required tabs, then call Create once (add)
   * or Update once (edit) with the full multi-tab payload.
   */
  const handleFinalSave = async () => {
    const tabsToValidate = ['basic', 'contacts', 'tax', 'banking', 'commercial', 'internal'];
    let mergedErrors = {};
    for (const tabId of tabsToValidate) {
      mergedErrors = { ...mergedErrors, ...validateTab(tabId) };
    }

    if (Object.keys(mergedErrors).length > 0) {
      setErrors(mergedErrors);
      setValidationAlert(i18n.t('inventory.validationFailedReviewTabs'));
      const firstErrorTab = tabs.find((tab) => {
        if (tab.id === 'basic') return !!(mergedErrors.tradeName || mergedErrors.vendorType);
        if (tab.id === 'contacts') {
          return !!(mergedErrors.primaryName || mergedErrors.primaryMobile || mergedErrors.primaryEmail);
        }
        if (tab.id === 'tax') return !!(mergedErrors.gstin || mergedErrors.pan);
        if (tab.id === 'banking') {
          return !!(mergedErrors.bankName || mergedErrors.accountNumber || mergedErrors.ifscCode);
        }
        if (tab.id === 'commercial') return !!(mergedErrors.paymentTerms || mergedErrors.currency);
        if (tab.id === 'internal') return !!(mergedErrors.vendorStatus || mergedErrors.riskLevel);
        return false;
      });
      if (firstErrorTab) setActiveTab(firstErrorTab.id);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const formPayload = buildFormPayload();

    setSaving(true);
    setValidationAlert(null);
    try {
      // createdBy is required on create only (ProductVendorCreateRequest); update omits it.
      if (!isEditRoute) {
        const apiBody = mapFormToApiPayload(formPayload, { forUpdate: false });
        if (apiBody.createdBy == null || apiBody.createdBy === '') {
          setValidationAlert(i18n.t('inventory.missingEmployeeIdSession'));
          setSaving(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
      }

      const saved = isEditRoute
        ? await updateVendor(vendorId, formPayload)
        : await createVendor(formPayload);

      const savedId = saved?.id || saved?.vendorId || vendorId;

      // PATCH /product-vendors/approve/{vendorId} records the approver on the vendor.
      let approvalWarning = '';
      if (approvedBy && savedId) {
        try {
          await approveVendor(savedId, { approvedBy, approvalDate });
        } catch (approveErr) {
          approvalWarning = i18n.t('inventory.approvalCouldNotBeRecorded', {
            error: approveErr?.message || i18n.t('inventory.approveRequestFailed'),
          });
        }
      }

      sessionStorage.setItem(
        TOAST_KEY,
        JSON.stringify({
          message: `${isEditRoute ? i18n.t('inventory.vendorUpdatedSuccess') : i18n.t('inventory.vendorCreatedSuccess')}${approvalWarning}`,
          type: approvalWarning ? 'error' : 'success',
        })
      );
      navigate(`/IMS/inventory/vendors/${savedId}`);
    } catch (err) {
      setValidationAlert(err?.message || i18n.t('inventory.failedToSaveVendor'));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AppLayout formLayout="standard">
        <div className="inventory-module">
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <p style={{ color: 'var(--text-grey)', fontSize: '14px' }}>{i18n.t('inventory.loadingVendor')}</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (loadError) {
    return (
      <AppLayout formLayout="standard">
        <div className="inventory-module">
          <div style={{ padding: '40px', textAlign: 'center', backgroundColor: '#ffffff', borderRadius: '16px', border: '1px solid var(--border-color)' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '700', color: 'var(--primary-navy)' }}>{loadError}</h3>
            <button type="button" className="btn-secondary" style={{ marginTop: '16px' }} onClick={() => navigate('/IMS/inventory/vendors')}>
              {i18n.t('inventory.backToVendorCatalog')}
            </button>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout formLayout="standard">
      <div className="inventory-module">
    <div>
      
      {/* PAGE HEADER */}
      <div className="flex flex-col gap-2" style={{ marginBottom: '20px' }}>

        <PageHeader back={<PageBackLink to="/IMS/inventory/vendors" />}
          className="mb-0"
          title={
            isEditRoute
              ? `${i18n.t('crm.editProfile')} ${tradeName || legalCompanyName || i18n.t('inventory.editVendor')}`
              : i18n.t('inventory.addVendor')
          }
        />
      </div>

      {/* VALIDATION ALERT */}
      {validationAlert && (
        <div className="form-warning-alert" style={{ marginBottom: '20px', display: 'flex', gap: '12px', padding: '12px 16px', backgroundColor: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', color: '#991b1b', alignItems: 'center' }}>
          <AlertTriangle size={18} style={{ flexShrink: 0 }} />
          <span style={{ fontSize: '13px', fontWeight: '600' }}>{validationAlert}</span>
        </div>
      )}

      {/* TABS SELECTOR — CRM Add Customer style, single scrollable row */}
      <div className="sticky top-0 z-20 bg-workspace pb-2">
        <div
          className="flex flex-nowrap gap-2 overflow-x-auto border-b border-(--color-text-border)/50 pb-2"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            const hasErrors = hasTabErrors(tab.id);
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`relative flex shrink-0 items-center gap-1.5 px-4 py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition ${
                  isActive
                    ? 'bg-primary text-white shadow-[0_4px_12px_rgba(30,51,131,0.2)]'
                    : 'hover:bg-workspace per-file-hint hover:text-foreground'
                }`}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span className="whitespace-nowrap">{tab.label}</span>
                {hasErrors && (
                  <span
                    className="absolute top-1.5 right-1.5 h-1.5 w-1.5 rounded-full bg-(--color-error,#ef4444)"
                    aria-hidden
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* TABBED FORM CARD */}
      <div className="form-surface" style={{ overflow: 'hidden' }}>
        {/* TAB PANELS */}
        <div className="inv-tab-panel" style={{ padding: '32px' }}>
          
          {/* TAB 1: BASIC INFORMATION */}
          {activeTab === 'basic' && (
            <div className="form-container-constrained">
              <div className="inv-responsive-grid">
                <div className="form-group">
                  <label className="form-label" style={{ fontWeight: '600' }}>{i18n.t('inventory.tradeName')} *</label>
                  <input 
                    type="text" 
                    className="form-input" 
                    placeholder={i18n.t('inventory.egTradeName')}
                    value={tradeName}
                    onChange={(e) => {
                      setTradeName(e.target.value);
                      if (errors.tradeName) setErrors({ ...errors, tradeName: null });
                    }}
                    style={{ borderColor: errors.tradeName ? 'var(--accent-red)' : '' }}
                  />
                  {errors.tradeName && <span style={{ fontSize: '11px', color: 'var(--accent-red)', marginTop: '4px', display: 'block' }}>{errors.tradeName}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label" style={{ fontWeight: '600' }}>{i18n.t('inventory.legalCompanyName')}</label>
                  <input 
                    type="text" 
                    className="form-input" 
                    placeholder={i18n.t('inventory.egLegalCompanyName')}
                    value={legalCompanyName}
                    onChange={(e) => setLegalCompanyName(e.target.value)}
                  />
                </div>
              </div>

              <div className="inv-responsive-grid">
                <div className="form-group">
                  <label className="form-label" style={{ fontWeight: '600' }}>{i18n.t('inventory.vendorType')} *</label>
                  <FormSelect
                    className="form-select"
                    value={vendorType}
                    onChange={(e) => {
                      setVendorType(e.target.value);
                      if (errors.vendorType) setErrors({ ...errors, vendorType: null });
                    }}
                    style={{ borderColor: errors.vendorType ? 'var(--accent-red)' : '', height: '40px' }}
                  >
                    <option value="">{i18n.t('fsm.selectType')}</option>
                    {vendorTypeOptions.map((opt) => {
                      // Bind option value to the label string (Manufacturer | OEM | Distributor), never the numeric id.
                      const optionValue = opt.label || opt.value;
                      return (
                        <option key={optionValue} value={optionValue}>
                          {translateOptionLabel(optionValue, opt.label)}
                        </option>
                      );
                    })}
                  </FormSelect>
                  {/* <span style={{ fontSize: '11px', color: 'var(--text-grey)', marginTop: '4px', display: 'block' }}>
                    Manufacturer and OEM vendors appear in the product Manufacturer dropdown.
                  </span> */}
                  {errors.vendorType && <span style={{ fontSize: '11px', color: 'var(--accent-red)', marginTop: '4px', display: 'block' }}>{errors.vendorType}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label" style={{ fontWeight: '600' }}>{i18n.t('inventory.companyRegistrationNumber')}</label>
                  <input 
                    type="text" 
                    className="form-input" 
                    placeholder={i18n.t('inventory.egCompanyRegNumber')}
                    value={companyRegNumber}
                    onChange={(e) => setCompanyRegNumber(e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: CONTACTS */}
          {activeTab === 'contacts' && (
            <div className="form-container-constrained" style={{ gap: '28px' }}>
              
              {/* Primary Contact Group */}
              <div>
                <h4 style={{ fontSize: '14px', fontWeight: '700', color: 'var(--primary-navy)', borderBottom: '1px solid var(--border-color)', paddingBottom: '8px', marginBottom: '16px' }}>{i18n.t('inventory.primaryContactMandatory')}</h4>
                <div className="inv-responsive-grid inv-responsive-grid--3">
                  <div className="form-group">
                    <label className="form-label">{i18n.t('inventory.contactName')} *</label>
                    <input 
                      type="text" 
                      className="form-input" 
                      placeholder={i18n.t('inventory.egContactRamesh')}
                      value={primaryName}
                      onChange={(e) => {
                        setPrimaryName(e.target.value);
                        if (errors.primaryName) setErrors({ ...errors, primaryName: null });
                      }}
                      style={{ borderColor: errors.primaryName ? 'var(--accent-red)' : '' }}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{i18n.t('inventory.mobileNumber')} *</label>
                    <input 
                      type="text" 
                      className="form-input" 
                      placeholder={i18n.t('inventory.egMobile9876543210')}
                      value={primaryMobile}
                      onChange={(e) => {
                        setPrimaryMobile(e.target.value);
                        if (errors.primaryMobile) setErrors({ ...errors, primaryMobile: null });
                      }}
                      style={{ borderColor: errors.primaryMobile ? 'var(--accent-red)' : '' }}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{i18n.t('auth.emailAddress')} *</label>
                    <input 
                      type="email" 
                      className="form-input" 
                      placeholder={i18n.t('inventory.egEmailRamesh')}
                      value={primaryEmail}
                      onChange={(e) => {
                        setPrimaryEmail(e.target.value);
                        if (errors.primaryEmail) setErrors({ ...errors, primaryEmail: null });
                      }}
                      style={{ borderColor: errors.primaryEmail ? 'var(--accent-red)' : '' }}
                    />
                  </div>
                </div>
              </div>

              {/* Secondary Contact Group */}
              <div>
                <h4 style={{ fontSize: '14px', fontWeight: '700', color: 'var(--primary-navy)', borderBottom: '1px solid var(--border-color)', paddingBottom: '8px', marginBottom: '16px' }}>{i18n.t('inventory.secondaryContactOptional')}</h4>
                <div className="inv-responsive-grid inv-responsive-grid--3">
                  <div className="form-group">
                    <label className="form-label">{i18n.t('inventory.contactName')}</label>
                    <input 
                      type="text" 
                      className="form-input" 
                      placeholder={i18n.t('inventory.egContactSunil')}
                      value={secondaryName}
                      onChange={(e) => setSecondaryName(e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{i18n.t('inventory.mobileNumber')}</label>
                    <input 
                      type="text" 
                      className="form-input" 
                      placeholder={i18n.t('inventory.egMobile9811223344')}
                      value={secondaryMobile}
                      onChange={(e) => setSecondaryMobile(e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{i18n.t('inventory.emailAddress')}</label>
                    <input 
                      type="email" 
                      className="form-input" 
                      placeholder={i18n.t('inventory.egEmailSunil')}
                      value={secondaryEmail}
                      onChange={(e) => setSecondaryEmail(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Alternate & Address */}
              <div>
                <h4 style={{ fontSize: '14px', fontWeight: '700', color: 'var(--primary-navy)', borderBottom: '1px solid var(--border-color)', paddingBottom: '8px', marginBottom: '16px' }}>{i18n.t('inventory.addressAndAltPhone')}</h4>
                
                <div className="form-group" style={{ marginBottom: '16px' }}>
                  <label className="form-label">{i18n.t('inventory.officePhoneAlternate')}</label>
                  <input 
                    type="text" 
                    className="form-input" 
                    placeholder={i18n.t('inventory.egOfficePhone')}
                    value={alternatePhone}
                    onChange={(e) => setAlternatePhone(e.target.value)}
                    style={{ maxWidth: '320px' }}
                  />
                </div>

                <div className="inv-responsive-grid inv-responsive-grid--2" style={{ marginBottom: '16px' }}>
                  <div className="form-group">
                    <label className="form-label">{i18n.t('inventory.registeredAddressLine1')}</label>
                    <input 
                      type="text" 
                      className="form-input" 
                      placeholder={i18n.t('inventory.egAddressLine1')}
                      value={addressLine1}
                      onChange={(e) => setAddressLine1(e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{i18n.t('inventory.registeredAddressLine2')}</label>
                    <input 
                      type="text" 
                      className="form-input" 
                      placeholder={i18n.t('inventory.egAddressLine2')}
                      value={addressLine2}
                      onChange={(e) => setAddressLine2(e.target.value)}
                    />
                  </div>
                </div>

                <div className="inv-responsive-grid inv-responsive-grid--3">
                  <div className="form-group">
                    <label className="form-label">{i18n.t('crm.city')}</label>
                    <input 
                      type="text" 
                      className="form-input" 
                      placeholder={i18n.t('inventory.egCityMumbai')}
                      value={addressCity}
                      onChange={(e) => setAddressCity(e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{i18n.t('crm.state')}</label>
                    <input 
                      type="text" 
                      className="form-input" 
                      placeholder={i18n.t('inventory.egStateMaharashtra')}
                      value={addressState}
                      onChange={(e) => setAddressState(e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{i18n.t('inventory.pinCode')}</label>
                    <input 
                      type="text" 
                      className="form-input" 
                      placeholder={i18n.t('inventory.egPin400072')}
                      value={addressPincode}
                      onChange={(e) => setAddressPincode(e.target.value)}
                    />
                  </div>
                </div>
              </div>

            </div>
          )}

          {/* TAB 3: TAX & COMPLIANCE */}
          {activeTab === 'tax' && (
            <div className="form-container-constrained">
              <div className="inv-responsive-grid">
                <div className="form-group">
                  <label className="form-label" style={{ fontWeight: '600' }}>{i18n.t('crm.gstin')} *</label>
                  <input 
                    type="text" 
                    className="form-input" 
                    placeholder={i18n.t('inventory.egGstin')}
                    value={gstin}
                    onChange={(e) => {
                      setGstin(e.target.value);
                      if (errors.gstin) setErrors({ ...errors, gstin: null });
                    }}
                    style={{ borderColor: errors.gstin ? 'var(--accent-red)' : '' }}
                  />
                  {errors.gstin && <span style={{ fontSize: '11px', color: 'var(--accent-red)', marginTop: '4px', display: 'block' }}>{errors.gstin}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label" style={{ fontWeight: '600' }}>{i18n.t('crm.pan')} *</label>
                  <input 
                    type="text" 
                    className="form-input" 
                    placeholder={i18n.t('examples.eGAbcde1234f')}
                    value={pan}
                    onChange={(e) => {
                      setPan(e.target.value);
                      if (errors.pan) setErrors({ ...errors, pan: null });
                    }}
                    style={{ borderColor: errors.pan ? 'var(--accent-red)' : '' }}
                  />
                  {errors.pan && <span style={{ fontSize: '11px', color: 'var(--accent-red)', marginTop: '4px', display: 'block' }}>{errors.pan}</span>}
                </div>
              </div>

              <div className="inv-responsive-grid">
                <div className="form-group">
                  <label className="form-label">{i18n.t('inventory.msmeUdyamRegNo')}</label>
                  <input 
                    type="text" 
                    className="form-input" 
                    placeholder={i18n.t('inventory.egMsme')}
                    value={msmeRegNo}
                    onChange={(e) => setMsmeRegNo(e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">{i18n.t('inventory.cin')}</label>
                  <input 
                    type="text" 
                    className="form-input" 
                    placeholder={i18n.t('inventory.egCin')}
                    value={cin}
                    onChange={(e) => setCin(e.target.value)}
                  />
                </div>
              </div>

              <div className="inv-responsive-grid">
                <div className="form-group">
                  <label className="form-label">{i18n.t('inventory.tan')}</label>
                  <input 
                    type="text" 
                    className="form-input" 
                    placeholder={i18n.t('inventory.egTan')}
                    value={tan}
                    onChange={(e) => setTan(e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">{i18n.t('inventory.iec')}</label>
                  <input 
                    type="text" 
                    className="form-input" 
                    placeholder={i18n.t('inventory.egIec')}
                    value={iec}
                    onChange={(e) => setIec(e.target.value)}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">{i18n.t('inventory.isoOtherCertifications')}</label>
                <input 
                  type="text" 
                  className="form-input" 
                  placeholder={i18n.t('inventory.egIsoCerts')}
                  value={isoCertifications}
                  onChange={(e) => setIsoCertifications(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* TAB 4: BANKING DETAILS */}
          {activeTab === 'banking' && (
            <div className="form-container-constrained">
              {vendorType && vendorType !== 'Distributor' ? (
                <div style={{ padding: '10px 12px', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '6px', color: '#1e3a8a', fontSize: '12px', fontWeight: '500', marginBottom: '16px' }}>
                  {i18n.t('inventory.bankingRelaxedForType', { vendorType: translateSupplierType(vendorType) })}
                </div>
              ) : (
                <div style={{ padding: '10px 12px', backgroundColor: '#fef3c7', border: '1px solid #fde68a', borderRadius: '6px', color: '#78350f', fontSize: '12px', fontWeight: '500', marginBottom: '16px' }}>
                  {i18n.t('inventory.bankingMandatoryDistributor')}
                </div>
              )}

              <div className="inv-responsive-grid">
                <div className="form-group">
                  <label className="form-label" style={{ fontWeight: '600' }}>
                    {i18n.t('inventory.bankNameBranch')} {vendorType === 'Distributor' ? '*' : ''}
                  </label>
                  <input 
                    type="text" 
                    className="form-input" 
                    placeholder={i18n.t('inventory.egBankBranch')}
                    value={bankName}
                    onChange={(e) => {
                      setBankName(e.target.value);
                      if (errors.bankName) setErrors({ ...errors, bankName: null });
                    }}
                    style={{ borderColor: errors.bankName ? 'var(--accent-red)' : '' }}
                  />
                  {errors.bankName && <span style={{ fontSize: '11px', color: 'var(--accent-red)', marginTop: '4px', display: 'block' }}>{errors.bankName}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label" style={{ fontWeight: '600' }}>
                    {i18n.t('inventory.accountNameNumber')} {vendorType === 'Distributor' ? '*' : ''}
                  </label>
                  <input 
                    type="text" 
                    className="form-input" 
                    placeholder={i18n.t('inventory.egAccountNumber')}
                    value={accountNumber}
                    onChange={(e) => {
                      setAccountNumber(e.target.value);
                      if (errors.accountNumber) setErrors({ ...errors, accountNumber: null });
                    }}
                    style={{ borderColor: errors.accountNumber ? 'var(--accent-red)' : '' }}
                  />
                  {errors.accountNumber && <span style={{ fontSize: '11px', color: 'var(--accent-red)', marginTop: '4px', display: 'block' }}>{errors.accountNumber}</span>}
                </div>
              </div>

              <div className="inv-responsive-grid">
                <div className="form-group">
                  <label className="form-label" style={{ fontWeight: '600' }}>
                    {i18n.t('inventory.ifscCode')} {vendorType === 'Distributor' ? '*' : ''}
                  </label>
                  <input 
                    type="text" 
                    className="form-input" 
                    placeholder={i18n.t('inventory.egIfsc')}
                    value={ifscCode}
                    onChange={(e) => {
                      setIfscCode(e.target.value);
                      if (errors.ifscCode) setErrors({ ...errors, ifscCode: null });
                    }}
                    style={{ borderColor: errors.ifscCode ? 'var(--accent-red)' : '' }}
                  />
                  {errors.ifscCode && <span style={{ fontSize: '11px', color: 'var(--accent-red)', marginTop: '4px', display: 'block' }}>{errors.ifscCode}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">{i18n.t('inventory.swift')}</label>
                  <input 
                    type="text" 
                    className="form-input" 
                    placeholder={i18n.t('inventory.egSwift')}
                    value={swiftCode}
                    onChange={(e) => setSwiftCode(e.target.value)}
                  />
                </div>
              </div>

              <div className="inv-responsive-grid">
                <div className="form-group">
                  <label className="form-label">{i18n.t('inventory.upiId')}</label>
                  <input 
                    type="text" 
                    className="form-input" 
                    placeholder={i18n.t('inventory.egUpi')}
                    value={upiId}
                    onChange={(e) => setUpiId(e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}

          {/* TAB 5: BUSINESS DETAILS */}
          {activeTab === 'business' && (
            <div className="form-container-constrained">
              <div className="form-group">
                <label className="form-label">{i18n.t('inventory.productsServicesOffered')}</label>
                <textarea 
                  className="form-input" 
                  style={{ minHeight: '100px', padding: '10px 12px', resize: 'vertical' }}
                  placeholder={i18n.t('inventory.egProductsOffered')}
                  value={productsOffered}
                  onChange={(e) => setProductsOffered(e.target.value)}
                />
              </div>

              <div className="form-group" style={{ maxWidth: '320px' }}>
                <label className="form-label">{i18n.t('inventory.authorizedDealerOem')}</label>
                <FormSelect
                  className="form-select"
                  value={isAuthorized}
                  onChange={(e) => setIsAuthorized(e.target.value)}
                  style={{ height: '40px' }}
                >
                  <option value="">{i18n.t('inventory.selectOption')}</option>
                  <option value="Yes">{i18n.t('common.yes')}</option>
                  <option value="No">{i18n.t('common.no')}</option>
                </FormSelect>
              </div>
            </div>
          )}

          {/* TAB 6: COMMERCIAL DETAILS */}
          {activeTab === 'commercial' && (
            <div className="form-container-constrained">
              
              {vendorType && vendorType !== 'Distributor' ? (
                <div style={{ padding: '10px 12px', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '6px', color: '#1e3a8a', fontSize: '12px', fontWeight: '500', marginBottom: '16px' }}>
                  {i18n.t('inventory.paymentTermsOptionalManufacturerOem')}
                </div>
              ) : (
                <div style={{ padding: '10px 12px', backgroundColor: '#fef3c7', border: '1px solid #fde68a', borderRadius: '6px', color: '#78350f', fontSize: '12px', fontWeight: '500', marginBottom: '16px' }}>
                  {i18n.t('inventory.paymentTermsMandatoryDistributor')}
                </div>
              )}

              <div className="form-group">
                <label className="form-label" style={{ fontWeight: '600' }}>
                  {i18n.t('inventory.standardPaymentTerms')} {vendorType === 'Distributor' ? '*' : ''}
                </label>
                <input 
                  type="text" 
                  className="form-input" 
                  placeholder={i18n.t('inventory.egPaymentTerms')}
                  value={paymentTerms}
                  onChange={(e) => {
                    setPaymentTerms(e.target.value);
                    if (errors.paymentTerms) setErrors({ ...errors, paymentTerms: null });
                  }}
                  style={{ borderColor: errors.paymentTerms ? 'var(--accent-red)' : '' }}
                />
                {errors.paymentTerms && <span style={{ fontSize: '11px', color: 'var(--accent-red)', marginTop: '4px', display: 'block' }}>{errors.paymentTerms}</span>}
              </div>

              <div className="inv-responsive-grid">
                <div className="form-group">
                  <label className="form-label">{i18n.t('inventory.standardCreditPeriodDays')}</label>
                  <input 
                    type="number" 
                    className="form-input" 
                    placeholder={i18n.t('inventory.egCreditDays30')}
                    value={creditPeriod}
                    onChange={(e) => setCreditPeriod(e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" style={{ fontWeight: '600' }}>{i18n.t('productMetrics.currency')} *</label>
                  <FormSelect
                    className="form-select"
                    value={currency}
                    onChange={(e) => {
                      setCurrency(e.target.value);
                      if (errors.currency) setErrors({ ...errors, currency: null });
                    }}
                    style={{ borderColor: errors.currency ? 'var(--accent-red)' : '', height: '40px' }}
                    disabled={lookupsLoading && currencyOptions.length === 0}
                  >
                    <option value="">
                      {lookupsLoading && currencyOptions.length === 0
                        ? i18n.t('inventory.loadingCurrencies')
                        : currencyOptions.length === 0
                          ? i18n.t('inventory.noCurrenciesAvailable')
                          : i18n.t('inventory.selectCurrency')}
                    </option>
                    {currencyOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </FormSelect>
                  {errors.currency && <span style={{ fontSize: '11px', color: 'var(--accent-red)', marginTop: '4px', display: 'block' }}>{errors.currency}</span>}
                </div>
              </div>
            </div>
          )}

          {/* TAB 7: DOCUMENTS */}
          {activeTab === 'documents' && (
            <div className="form-container-constrained">
              <h4 style={{ fontSize: '14px', fontWeight: '700', color: 'var(--primary-navy)', borderBottom: '1px solid var(--border-color)', paddingBottom: '8px', marginBottom: '16px' }}>{i18n.t('inventory.registrationKycDocuments')}</h4>

              <input 
                id="vendor-document-file-input" 
                type="file" 
                multiple 
                accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" 
                style={{ display: 'none' }}
                onChange={handleFileInputChange}
              />

              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragActive(true);
                }}
                onDragLeave={() => setDragActive(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragActive(false);
                  if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                    handleFileSelect(e.dataTransfer.files);
                  }
                }}
                onClick={() => document.getElementById('vendor-document-file-input').click()}
                style={{
                  border: dragActive ? '2px dashed var(--accent-blue)' : '2px dashed var(--border-color)',
                  backgroundColor: dragActive ? '#eff6ff' : '#f8fafc',
                  borderRadius: '12px',
                  padding: '24px 16px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  marginBottom: '20px',
                  transition: 'all 0.2s ease'
                }}
              >
                <Upload size={28} style={{ color: 'var(--accent-blue)', marginBottom: '8px' }} />
                <p style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)' }}>
                  {i18n.t('inventory.clickToAttachDocuments')}
                </p>
                <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-grey)' }}>
                  {i18n.t('inventory.attachKycFilesHint')}
                </p>
              </div>

              {uploadedFiles.length === 0 ? (
                <div style={{ border: '1px solid var(--border-color)', padding: '24px 16px', borderRadius: '12px', textAlign: 'center', color: 'var(--text-light)', backgroundColor: '#ffffff' }}>
                  <FileText size={32} style={{ marginBottom: '8px', opacity: 0.5 }} />
                  <p style={{ margin: 0, fontSize: '13px' }}>{i18n.t('inventory.noKycDocumentsYet')}</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {uploadedFiles.map((file) => (
                    <div 
                      key={file.id} 
                      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', border: '1px solid var(--border-color)', borderRadius: '8px', backgroundColor: '#ffffff' }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <FileText size={18} style={{ color: 'var(--accent-blue)' }} />
                        <div>
                          <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-dark)' }}>{file.name}</span>
                          <span style={{ fontSize: '11px', color: 'var(--text-grey)', marginLeft: '8px' }}>
                            {file.size ? `${file.size} • ` : ''}{i18n.t('inventory.uploadedDate', { date: file.uploadedAt })}
                          </span>
                        </div>
                      </div>
                      <button 
                        type="button" 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveFile(file.id);
                        }}
                        style={{ border: 'none', background: 'none', color: 'var(--accent-red)', cursor: 'pointer', padding: '4px' }}
                        title={i18n.t('inventory.removeDocument')}
                      >
                        <Trash size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* TAB 8: INTERNAL */}
          {activeTab === 'internal' && (
            <div className="form-container-constrained">
              <div className="inv-responsive-grid">
                <div className="form-group">
                  <label className="form-label" style={{ fontWeight: '600' }}>{i18n.t('inventory.vendorStatus')} *</label>
                  <FormSelect
                    className="form-select"
                    value={vendorStatus}
                    onChange={(e) => {
                      setVendorStatus(e.target.value);
                      if (errors.vendorStatus) setErrors({ ...errors, vendorStatus: null });
                    }}
                    style={{ borderColor: errors.vendorStatus ? 'var(--accent-red)' : '', height: '40px' }}
                    disabled={lookupsLoading && vendorStatusOptions.length === 0}
                  >
                    <option value="">
                      {lookupsLoading && vendorStatusOptions.length === 0
                        ? i18n.t('inventory.loadingStatuses')
                        : vendorStatusOptions.length === 0
                          ? i18n.t('inventory.noStatusesAvailable')
                          : i18n.t('inventory.selectVendorStatus')}
                    </option>
                    {vendorStatusOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{translateOptionLabel(opt.value, opt.label)}</option>
                    ))}
                  </FormSelect>
                  {errors.vendorStatus && <span style={{ fontSize: '11px', color: 'var(--accent-red)', marginTop: '4px', display: 'block' }}>{errors.vendorStatus}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">{i18n.t('inventory.riskLevelLabel')} *</label>
                  <FormSelect
                    className="form-select"
                    value={riskLevel}
                    onChange={(e) => {
                      setRiskLevel(e.target.value);
                      if (errors.riskLevel) setErrors({ ...errors, riskLevel: null });
                    }}
                    style={{ borderColor: errors.riskLevel ? 'var(--accent-red)' : '', height: '40px' }}
                    disabled={lookupsLoading && riskLevelOptions.length === 0}
                  >
                    <option value="">
                      {lookupsLoading
                        ? i18n.t('inventory.loadingRiskLevels')
                        : riskLevelOptions.length === 0
                          ? i18n.t('inventory.noRiskLevelsAvailable')
                          : i18n.t('inventory.selectRiskLevel')}
                    </option>
                    {riskLevelOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{translateOptionLabel(opt.value, opt.label)}</option>
                    ))}
                  </FormSelect>
                  {errors.riskLevel && <span style={{ fontSize: '11px', color: 'var(--accent-red)', marginTop: '4px', display: 'block' }}>{errors.riskLevel}</span>}
                </div>
              </div>

              <div className="inv-responsive-grid">
                <div className="form-group">
                  <label className="form-label">{i18n.t('inventory.approvedBy')}</label>
                  <FormSelect
                    className="form-select"
                    value={approvedBy}
                    onChange={(e) => setApprovedBy(e.target.value)}
                    style={{ height: '40px' }}
                    disabled={lookupsLoading && approverOptions.length === 0}
                  >
                    <option value="">
                      {lookupsLoading && approverOptions.length === 0
                        ? i18n.t('inventory.loadingApprovers')
                        : approverOptions.length === 0
                          ? i18n.t('inventory.noApproversAvailable')
                          : i18n.t('inventory.selectApprover')}
                    </option>
                    {approverOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </FormSelect>
                  {/* <span style={{ fontSize: '11px', color: 'var(--text-grey)', marginTop: '4px', display: 'block' }}>
                    Approving a vendor records the selected employee against the vendor.
                  </span> */}
                </div>

                <div className="form-group">
                  <label className="form-label">{i18n.t('inventory.approvalDate')}</label>
                  <input 
                    type="date" 
                    className="form-input" 
                    value={approvalDate}
                    onChange={(e) => setApprovalDate(e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}

        </div>

        {/* FOOTER BUTTON ACTION BAR */}
        <div className="inv-form-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', padding: '16px 32px', backgroundColor: '#fafbfd', borderTop: '1px solid var(--border-color)', flexWrap: 'wrap' }}>
          <button 
            type="button" 
            onClick={onCancel}
            disabled={saving}
            style={{ 
              height: '36px', 
              borderRadius: '6px', 
              padding: '0 16px', 
              fontWeight: '600',
              fontSize: '13px',
              border: '1px solid var(--border-color)',
              backgroundColor: '#ffffff',
              color: 'var(--text-dark)',
              cursor: saving ? 'not-allowed' : 'pointer',
              transition: 'var(--transition-all)',
              opacity: saving ? 0.7 : 1
            }}
          >
            {i18n.t('formActions.cancel')}
          </button>
          {!isFirstTab && (
            <button
              type="button"
              onClick={handleBackTab}
              disabled={saving}
              style={{
                height: '36px',
                borderRadius: '6px',
                padding: '0 16px',
                fontWeight: '600',
                fontSize: '13px',
                border: '1px solid var(--border-color)',
                backgroundColor: '#ffffff',
                color: 'var(--text-dark)',
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.7 : 1,
              }}
            >
              {i18n.t('formActions.back')}
            </button>
          )}
          {!isLastTab ? (
            <button 
              type="button" 
              onClick={handleSaveAndNext}
              disabled={saving}
              style={{ 
                height: '36px', 
                borderRadius: '6px', 
                padding: '0 20px', 
                fontWeight: '600',
                fontSize: '13px',
                border: 'none',
                backgroundColor: 'var(--primary-navy, #1e293b)', 
                color: '#ffffff',
                cursor: saving ? 'not-allowed' : 'pointer',
                transition: 'var(--transition-all)',
                opacity: saving ? 0.7 : 1
              }}
            >
              {i18n.t('inventory.saveAndNext')}
            </button>
          ) : (
            <button 
              type="button" 
              onClick={handleFinalSave}
              disabled={saving}
              style={{ 
                height: '36px', 
                borderRadius: '6px', 
                padding: '0 20px', 
                fontWeight: '600',
                fontSize: '13px',
                border: 'none',
                backgroundColor: 'var(--primary-navy, #1e293b)', 
                color: '#ffffff',
                cursor: saving ? 'not-allowed' : 'pointer',
                transition: 'var(--transition-all)',
                opacity: saving ? 0.7 : 1
              }}
            >
              {saving
                ? (isEditRoute ? i18n.t('inventory.updatingEllipsis') : i18n.t('inventory.creatingEllipsis'))
                : (isEditRoute ? i18n.t('inventory.saveVendor') : i18n.t('inventory.createVendor'))}
            </button>
          )}
        </div>

      </div>

    </div>
      </div>
    </AppLayout>
  );
}
